import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, Heading, Icon, Separator, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { useNavigate } from 'react-router-dom';

import { PerformanceChartSection } from './components/price-performance/PerformanceChartSection';
import {
  DirectTradingAccountSecurityDocument,
  DirectTradingAccountSecurityQuery,
} from './graphql/directTradingAccountSecurity.generated';

import { NarrowTypename } from '../../../graphql/utils/narrow-typename';
import { ThemedImage } from '../../../Shared/components-v2/themed-image/ThemedImage';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { Props } from '../../AccountSecurityPage';
import { iconNameForSymbol } from '../../utils';

type FinancialSecurity = NarrowTypename<
  DirectTradingAccountSecurityQuery['account'],
  'DirectTradingAccount'
>['financialSecurity'];

const iconDisplayForSecurity = (security: FinancialSecurity, symbol: string | null | undefined) => {
  const defaultIconElement = (
    <Icon
      color="currentColor"
      css={{ width: 'size.400', height: 'size.400' }}
      name={iconNameForSymbol(symbol)}
      size="medium"
    />
  );
  let iconElement = defaultIconElement;

  if (security && security.__typename === 'FinancialSecurityStock') {
    const { companyProfile } = security;
    const { logo } = companyProfile;

    if (logo) {
      iconElement = <ThemedImage css={{ width: 'size.400', height: 'size.400' }} alt="logo" themedAsset={logo} />;
    }
  }

  return (
    <Box
      css={{
        color: 'primitives.black',
        backgroundColor: 'primitives.grey.10',
        borderRadius: 'medium',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'size.600',
        height: 'size.600',
        overflow: 'hidden',
        padding: 'space.100',
      }}
    >
      {iconElement}
    </Box>
  );
};

export const OverviewCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });
  const navigate = useNavigate();

  const navigateToPreviousPage = () => {
    void navigate(-1);
  };

  if (!data) return <CardLoader />;
  if (!data.account || data.account.__typename !== 'DirectTradingAccount') {
    navigateToPreviousPage();
    return null;
  }

  const { account } = data;
  const { financialSecurity } = account;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <SambaAutoLink to={urlBuilder.appAccount.absolute({ accountId: account.id })}>
          <Box css={{ display: 'flex', alignItems: 'center', color: 'text' }}>
            <Icon name="CaretLeft" size="small" css={{ mr: 'space.100' }} />
            <Text>{account.name}</Text>
          </Box>
        </SambaAutoLink>
      </Card.Header>
      <Card.Content css={{ gap: 'space.300', display: 'grid' }}>
        <Box css={{ display: 'flex', verticalAlign: 'center', alignItems: 'center', gap: 'space.200' }}>
          {iconDisplayForSecurity(financialSecurity, financialSecurity.symbol)}
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Heading as="h3" variant="title300">
                {financialSecurity.name}
              </Heading>
              <Text color="text.weak" variant="text100">
                {financialSecurity.symbol}
              </Text>
            </Box>
          </Box>
        </Box>

        <Separator />

        <PerformanceChartSection accountId={accountId} securityId={securityId} />
      </Card.Content>
    </Card>
  );
};
